import { customerLabel } from '../../lib/customerName';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultDiscoveryFilter,
  filterDiscovery,
  groupDiscovery,
  type DiscoveryFilter,
} from '../../data/discoveryGroups';
import type { MarketingDiscoveredResource } from '../../data/marketingTypes';
import { fmtTime } from '../../lib/growthFormat';
import { PropertyRow } from './GoogleAccountsPage';

/**
 * The Google properties a sign-in can see, for picking (WP1.8): search,
 * grouped by Google account, properties another customer already uses
 * shown refused with the reason (rule 3, no override), and a refresh of the list (cached for up to an hour).
 *
 * With the server's groups (docs/19 section 5) it shows three: "Belongs
 * here" to tick for this customer, "Looks like other restaurants you can
 * open" with Connect, and "No restaurant found" with Choose restaurant;
 * both open the confirmation sheet. Nothing is ticked for the person.
 */
export function DiscoveryPicker({
  items,
  selected,
  onToggle,
  orphanIds,
  discoveredAt,
  busy = false,
  onRefresh,
  customerName,
  onConnect,
}: {
  items: MarketingDiscoveredResource[];
  selected: Record<string, boolean>;
  onToggle: (externalId: string) => void;
  /** Selected properties with no restaurant yet (a hint is shown). */
  orphanIds?: ReadonlySet<string>;
  /** Unused: every viewer sees properties another customer uses refused. */
  viewerIsStaff?: boolean;
  discoveredAt?: number | null;
  busy?: boolean;
  onRefresh?: () => void;
  /** The customer of the restaurant the wizard runs for, for "Belongs here". */
  customerName?: string | null;
  /** Opens the confirmation sheet for a property of another restaurant. */
  onConnect?: (item: MarketingDiscoveredResource, initialRestaurantId: string | null) => void;
}) {
  const { t } = useTranslation('growth');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<DiscoveryFilter>(() => defaultDiscoveryFilter(items));
  const grouped = Boolean(onConnect) && items.some(item => item.group);
  const chosen = useMemo(
    () => new Set(Object.keys(selected).filter(id => selected[id])),
    [selected],
  );
  const groups = useMemo(
    () => groupDiscovery(filterDiscovery(items, { query, filter: grouped ? 'all' : filter }, chosen)),
    [items, query, filter, chosen, grouped],
  );
  const matchCount = items.filter(item => item.matchesCustomer).length;
  const listedAt = discoveredAt ? fmtTime(discoveredAt) : null;
  const searched = useMemo(
    () => (grouped ? filterDiscovery(items, { query, filter: 'all' }, chosen) : []),
    [grouped, items, query, chosen],
  );

  const row = (item: MarketingDiscoveredResource) => {
    const on = !!selected[item.externalId];
    // Rule 3: a property feeding another customer can't be mapped here, for
    // staff too; there is no override. A tick left over can still be removed.
    const refused = Boolean(item.mappedElsewhere);
    const reasonId = refused ? `mkt-res-refused-${item.externalId.replace(/[^\w-]/g, '_')}` : undefined;
    return (
      <label
        key={item.externalId}
        className={`mkt-wizard-res${on ? ' on' : ''}${refused ? ' refused' : ''}`}
      >
        <input
          type="checkbox"
          checked={on}
          disabled={refused && !on}
          aria-describedby={reasonId}
          onChange={() => onToggle(item.externalId)}
        />
        <div>
          <div className="mkt-wizard-res-name">
            {item.displayName}
            {refused ? (
              <span className="mkt-discovery-badge">{t('discovery.usedElsewhere')}</span>
            ) : null}
          </div>
          <div className="mkt-wizard-res-id">{item.externalId}</div>
          {refused ? (
            <div id={reasonId} className="mkt-wizard-orphan">
              {t('discovery.feedsOtherReason')}
            </div>
          ) : null}
          {on && orphanIds?.has(item.externalId) ? (
            <div className="mkt-wizard-orphan">{t('discovery.orphan')}</div>
          ) : null}
        </div>
      </label>
    );
  };

  const tools = (
    <div className="mkt-discovery-tools">
      <input
        type="search"
        className="mkt-wizard-search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={t('discovery.search')}
        aria-label={t('discovery.search')}
      />
      {!grouped ? (
        <div className="mkt-wizard-filters" role="tablist" aria-label={t('discovery.filterAria')}>
          {(
            [
              ['matches', t('discovery.matches', { count: matchCount })],
              ['all', t('discovery.all', { count: items.length })],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              className={`growth-tab${filter === id ? ' on' : ''}`}
              aria-selected={filter === id}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {onRefresh ? (
        <span className="mkt-wizard-meta">
          {listedAt ? `${t('connect.listedAt', { when: listedAt })} · ` : ''}
          <button type="button" className="mkt-wizard-quiet" disabled={busy} onClick={onRefresh}>
            {t('connect.refresh')}
          </button>
        </span>
      ) : null}
    </div>
  );

  if (grouped && onConnect) {
    const customer = customerLabel(customerName) || t('accountConnect.thisRestaurant');
    const here = searched.filter(item => (item.group ?? 'here') === 'here');
    const elsewhere = searched.filter(item => item.group === 'elsewhere');
    const none = searched.filter(item => item.group === 'none');
    const asRow = (item: MarketingDiscoveredResource) => (
      <PropertyRow
        key={item.externalId}
        property={{
          kind: item.kind,
          externalId: item.externalId,
          displayName: item.displayName,
          accountLabel: item.accountLabel,
          feeds: item.feeds ?? null,
          suggestions: (item.elsewhereSuggestions || []).map(s => ({ id: s.id, name: s.name, city: s.city })),
        }}
        onConnect={initial => onConnect(item, initial)}
      />
    );
    return (
      <div className="mkt-discovery acx-groups">
        {tools}
        <section className="acx-group" data-acx-group="here" aria-labelledby="acx-group-here">
          <h3 id="acx-group-here" className="acx-group-title">
            {t('accountConnect.groupHere', { customer })}
          </h3>
          {here.length ? (
            <div className="mkt-discovery-group">{here.map(row)}</div>
          ) : (
            <p className="acx-group-empty">{t('accountConnect.groupHereEmpty', { customer })}</p>
          )}
        </section>
        {elsewhere.length ? (
          <section className="acx-group" data-acx-group="elsewhere" aria-labelledby="acx-group-elsewhere">
            <h3 id="acx-group-elsewhere" className="acx-group-title">
              {t('accountConnect.groupElsewhere')} ({elsewhere.length})
            </h3>
            <ul className="acx-list">{elsewhere.map(asRow)}</ul>
          </section>
        ) : null}
        {none.length ? (
          <section className="acx-group" data-acx-group="none" aria-labelledby="acx-group-none">
            <h3 id="acx-group-none" className="acx-group-title">
              {t('accountConnect.groupNone')} ({none.length})
            </h3>
            <ul className="acx-list">{none.map(asRow)}</ul>
          </section>
        ) : null}
        {!searched.length && items.length ? (
          <p className="mkt-wizard-empty" role="status">
            {t('discovery.noHits')}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mkt-discovery">
      {tools}
      {!groups.length && items.length ? (
        <p className="mkt-wizard-empty" role="status">
          {filter === 'matches' && !query.trim()
            ? t('discovery.noMatches')
            : t('discovery.noHits')}
        </p>
      ) : null}
      {groups.map(group => (
        <div key={group.key || 'none'} className="mkt-discovery-group">
          {group.label ? <div className="mkt-discovery-account">{group.label}</div> : null}
          {group.items.map(row)}
        </div>
      ))}
    </div>
  );
}
